import React, { useEffect, useRef, useCallback, useState } from 'react';
import CardGroup from '../CardGroup/CardGroup';
import { useDiscoverSubCategories } from '../../hooks/useDiscoverSubCategories';
import { useCardGroupPrefetch } from '../../hooks/useCardGroupPrefetch';
import styles from './DiscoverSubCategories.module.css';

const DiscoverSubCategories = ({
    componentSize = "Desktop 1440",
    selectedCategoryId = 9,
    excludeCardId = "",
    onCardSelect
}) => {
    const groupRefs = useRef({});
    const [visibleGroups, setVisibleGroups] = useState(new Set());
    
    const {
        cardGroups,
        containerWidth,
        isLoading,
        error
    } = useDiscoverSubCategories({
        componentSize,
        selectedCategoryId,
        excludeCardId
    });

    const {
        handleGroupVisibility
    } = useCardGroupPrefetch({
        groupSize: 5,
        maxConcurrentRequests: 2
    });

    const handleIntersection = useCallback((entries) => {
        entries.forEach(entry => {
            const groupIndex = parseInt(entry.target.dataset.groupIndex);
            
            setVisibleGroups(prev => {
                const updated = new Set(prev);
                if (entry.isIntersecting) {
                    updated.add(groupIndex);
                    handleGroupVisibility(groupIndex, cardGroups.length);
                } else {
                    updated.delete(groupIndex);
                }
                return updated;
            });
        });
    }, [handleGroupVisibility, cardGroups.length]);

    useEffect(() => {
        if (!cardGroups.length) return;

        const observer = new IntersectionObserver(handleIntersection, {
            root: null,
            rootMargin: '100px', // Aumentado para mejor anticipación
            threshold: 0.1
        });

        Object.values(groupRefs.current).forEach(ref => {
            if (ref) observer.observe(ref);
        });

        return () => observer.disconnect();
    }, [cardGroups.length, handleIntersection]);

    if (error || isLoading || cardGroups.length === 0) return null;

    return (
        <div
            className={styles.cardGroupsContainer}
            style={{ width: containerWidth }}
        >
            {cardGroups.map((group, groupIndex) => (
                <div
                    key={groupIndex}
                    ref={el => groupRefs.current[groupIndex] = el}
                    data-group-index={groupIndex}
                    className={styles.groupWrapper}
                >
                    <CardGroup
                        size={componentSize}
                        cardId1={group[0]?.id || ""}
                        cardId2={group[1]?.id || ""}
                        cardId3={group[2]?.id || ""}
                        cardId4={group[3]?.id || ""}
                        cardId5={group[4]?.id || ""}
                        groupPick12={(groupIndex % 12) + 1}
                        groupPick7={(groupIndex % 7) + 1}
                        cardCount={group.length}
                        onCardSelect={onCardSelect}
                        isVisible={visibleGroups.has(groupIndex)}
                    />
                </div>
            ))}
        </div>
    );
};

export default React.memo(DiscoverSubCategories);